import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Gift, Users, ShoppingBag, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useApp } from '../context/AppContext';

const InviteCodePage = () => {
  const { referralCode } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useApp();

  // Store referral code in localStorage for use during registration
  useEffect(() => {
    if (referralCode) {
      localStorage.setItem('minted_referral_code', referralCode);
    }
  }, [referralCode]);

  // If already logged in, redirect to home
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Look up the real inviter's name from their code
  const [inviterName, setInviterName] = useState('A friend');
  useEffect(() => {
    if (!referralCode) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/referrals/code/${referralCode}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => d?.name && setInviterName(d.name.split(' ')[0]))
      .catch(() => {});
  }, [referralCode]);
  const getInviterName = () => inviterName;

  return (
    <div className="min-h-screen bg-gradient-to-br from-minted-600 to-minted-700">
      {/* Header */}
      <div className="p-4">
        <div className="flex items-center gap-2 text-white">
          <ShoppingBag className="w-6 h-6" />
          <span className="font-bold text-lg">Minted</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 py-8 text-center text-white">
        <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <Gift className="w-10 h-10" />
        </div>
        
        <h1 className="text-3xl font-bold mb-3" data-testid="invite-inviter-heading">
          {getInviterName()} invited you!
        </h1>
        
        <p className="text-minted-100 text-lg mb-8">
          Join Minted — buy & sell preloved treasures, and earn <span className="font-bold text-white">$5</span> for every friend you invite
        </p>

        {/* Benefits */}
        <div className="bg-white/10 rounded-2xl p-6 mb-8 text-left">
          <h3 className="font-semibold text-lg mb-4">Why join Minted?</h3>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-minted-200 flex-shrink-0" />
              <span className="text-minted-100">Buy & sell locally near you</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-minted-200 flex-shrink-0" />
              <span className="text-minted-100">Secure payments with buyer protection</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-minted-200 flex-shrink-0" />
              <span className="text-minted-100">Easy listing with AI descriptions</span>
            </div>
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-minted-200 flex-shrink-0" />
              <span className="text-minted-100">Great deals on preloved items</span>
            </div>
          </div>
        </div>

        {/* Referral Code Display */}
        <div className="bg-white rounded-xl p-4 mb-6">
          <p className="text-sm text-gray-500 mb-1">Your referral code</p>
          <p className="text-2xl font-bold text-minted-600 tracking-wider">{referralCode}</p>
        </div>

        {/* CTA Buttons */}
        <Button 
          onClick={() => navigate('/register')}
          className="w-full bg-white text-minted-600 hover:bg-minted-50 font-semibold py-6 text-lg mb-3"
        >
          <Users className="w-5 h-5 mr-2" />
          Join Minted Now
        </Button>
        
        <p className="text-minted-200 text-sm">
          Already have an account?{' '}
          <button 
            onClick={() => navigate('/login')}
            className="text-white underline font-medium"
          >
            Sign In
          </button>
        </p>
      </div>

      {/* Footer */}
      <div className="absolute bottom-4 left-0 right-0 text-center">
        <p className="text-minted-200 text-xs">
          By joining, you agree to our{' '}
          <button onClick={() => navigate('/terms')} className="underline">Terms</button>
          {' '}and{' '}
          <button onClick={() => navigate('/privacy')} className="underline">Privacy Policy</button>
        </p>
      </div>
    </div>
  );
};

export default InviteCodePage;
